import { Suspense, lazy, useEffect, useState } from 'react';

import { Sheet, SheetContent } from '@/components/ui/sheet';
import { type ActiveFilters, type Filter, buildFilterPayload } from './filterTypes';

// Lazily, so the drill-down does not drag the table and @tanstack/react-table
// into the main bundle — LazyDataframe keeps it out of the chat path for the
// same reason.
const DrilldownTable = lazy(() =>
  import('@/components/Elements/Dataframe').then((m) => ({ default: m._DataframeElement }))
);

// Where a number comes from. The dashboard's own answer to "is this right?",
// which until now had none: widget SQL lives on the server and never reaches
// the client, so a wrong figure stayed invisible until someone reconciled by
// hand.
//
// The panel's job is to be believable, which means it has to be willing to say
// it does not know. Three things it must never do: show rows for a number it
// could not recompute, present an empty table as "there is nothing here" when
// the real cause is a label that did not match, or state a business meaning
// that no human ever wrote down.

export interface DrilldownTarget {
  widgetId: string;
  title: string;
  displayedValue: number | null;
  label: string | null;
}

interface DrilldownResponse {
  available: boolean;
  reason?: string;
  columns?: string[];
  rows?: (string | number)[][];
  total_rows?: number;
  page?: number;
  page_size?: number;
  reachable_rows?: number;
  capped?: boolean;
  ordered?: boolean;
  recomputed_value?: number | null;
  measure_expr?: string;
  dimension_expr?: string | null;
  bound_value?: string | null;
  chart_limit?: number | null;
  label_unmatched?: boolean;
  sql?: string;
  title?: string;
  type?: string;
  table?: string;
  definition?: string | null;
  computed_at?: string;
}

const fmt = (v: number | null | undefined) =>
  v == null ? '—' : v.toLocaleString(undefined, { maximumFractionDigits: 2 });

/** Reconciliation tolerance — float SUMs over thousands of rows do not land bit-identical. */
function reconciles(shown: number | null, recomputed: number | null | undefined): boolean {
  if (shown == null || recomputed == null) return true;
  const scale = Math.max(Math.abs(shown), Math.abs(recomputed), 1);
  return Math.abs(shown - recomputed) / scale < 1e-6;
}

export default function DrilldownPanel({
  target,
  filters,
  activeFilters,
  periodLabel,
  onClose
}: {
  target: DrilldownTarget | null;
  filters: Filter[];
  activeFilters: ActiveFilters;
  periodLabel: string | null;
  onClose: () => void;
}) {
  const [data, setData] = useState<DrilldownResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [showSql, setShowSql] = useState(false);

  const widgetId = target?.widgetId ?? null;
  const label = target?.label ?? null;

  useEffect(() => {
    setPage(0);
    setShowSql(false);
  }, [widgetId, label]);

  useEffect(() => {
    if (!widgetId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ widget_id: widgetId, page: String(page) });
    if (label != null) params.set('label', label);
    const payload = buildFilterPayload(filters, activeFilters);
    if (payload.length > 0) params.set('active_filters', JSON.stringify(payload));

    fetch(`/api/dashboard/drilldown?${params.toString()}`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d: DrilldownResponse) => {
        if (!cancelled) {
          setData(d);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [widgetId, label, page, activeFilters, filters]);

  const mismatch =
    data?.available && !reconciles(target?.displayedValue ?? null, data.recomputed_value);

  const totalPages = data?.reachable_rows && data.page_size
    ? Math.ceil(data.reachable_rows / data.page_size)
    : 0;

  return (
    <Sheet
      open={!!target}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <SheetContent
        side="right"
        className="w-full sm:max-w-3xl overflow-y-auto flex flex-col gap-4"
      >
        {target && (
          <>
            <div>
              <p className="text-xs text-muted-foreground">Where this number comes from</p>
              <h2 className="text-lg font-semibold mt-0.5">{target.title}</h2>
              <p className="text-3xl font-bold mt-1">{fmt(target.displayedValue)}</p>
              {label && (
                <p className="text-sm text-muted-foreground mt-0.5">for {label}</p>
              )}
            </div>

            {loading && (
              <p className="text-sm text-muted-foreground">Fetching the rows behind it…</p>
            )}
            {error && <Note tone="warn">Could not load: {error}</Note>}

            {data && !data.available && (
              <Note tone="warn">{data.reason ?? 'The detail for this widget is not available.'}</Note>
            )}

            {data?.available && (
              <>
                {/* The audit itself: the tile's own aggregate, re-run over the
                    same predicate. If these disagree, that IS the finding. */}
                {mismatch ? (
                  <Note tone="warn">
                    <span className="font-semibold">These do not match.</span> The tile shows{' '}
                    {fmt(target.displayedValue)} but re-running its own measure over the same
                    rows right now gives {fmt(data.recomputed_value)}. The tile may be showing a
                    cached figure, or the underlying data changed. Trust neither until checked.
                  </Note>
                ) : (
                  <Note tone="ok">
                    Re-running this tile's own measure over these {fmt(data.total_rows)} rows
                    gives {fmt(data.recomputed_value)} — it reconciles.
                  </Note>
                )}

                {data.label_unmatched && (
                  <Note tone="warn">
                    No rows matched <span className="font-mono">{label}</span>. The chart label is
                    converted to text before it reaches the browser, so a number or date bucket
                    may not compare equal to what is stored. This is not evidence that there is
                    no data.
                  </Note>
                )}

                {data.chart_limit != null && (
                  <Note tone="info">
                    This chart shows only its top {data.chart_limit}, so its bars do not add up to
                    a total over the whole table.
                  </Note>
                )}

                <Facts data={data} periodLabel={periodLabel} filters={filters} active={activeFilters} />

                <div>
                  <div className="flex items-baseline justify-between mb-2">
                    <p className="text-sm font-medium">
                      The rows behind it
                      <span className="text-muted-foreground font-normal">
                        {' '}
                        — {fmt(data.total_rows)} in total
                      </span>
                    </p>
                    {totalPages > 1 && (
                      <div className="flex items-center gap-2 text-xs">
                        <button
                          className="px-2 py-0.5 rounded border disabled:opacity-40"
                          disabled={page === 0 || loading}
                          onClick={() => setPage((p) => Math.max(0, p - 1))}
                        >
                          Prev
                        </button>
                        <span className="text-muted-foreground">
                          {page + 1} / {totalPages}
                        </span>
                        <button
                          className="px-2 py-0.5 rounded border disabled:opacity-40"
                          disabled={page + 1 >= totalPages || loading}
                          onClick={() => setPage((p) => p + 1)}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </div>

                  {data.rows && data.rows.length > 0 ? (
                    <div className="overflow-x-auto text-xs">
                      <Suspense
                        fallback={<p className="text-sm text-muted-foreground">Loading table…</p>}
                      >
                        <DrilldownTable
                          data={{
                            index: data.rows.map((_, i) => i),
                            columns: data.columns ?? [],
                            data: data.rows
                          }}
                        />
                      </Suspense>
                    </div>
                  ) : (
                    !data.label_unmatched && (
                      <p className="text-sm text-muted-foreground">No rows.</p>
                    )
                  )}

                  {data.capped && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Only the first {fmt(data.reachable_rows)} of {fmt(data.total_rows)} rows can
                      be paged through here. The total above and the reconciliation are computed
                      over all of them.
                    </p>
                  )}
                  {data.ordered === false && data.rows && data.rows.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      These rows have no guaranteed order — this table has no key to sort on — so
                      pages may not stay in the same sequence.
                    </p>
                  )}
                </div>

                {data.sql && (
                  <div className="flex items-center gap-2 pt-1">
                    <button
                      onClick={() => setShowSql((s) => !s)}
                      className="px-3 py-1.5 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                    >
                      {showSql ? 'Hide SQL' : 'Show SQL'}
                    </button>
                  </div>
                )}

                {showSql && data.sql && (
                  <pre className="text-[11px] bg-muted rounded-lg p-3 overflow-x-auto whitespace-pre-wrap">
                    {data.sql}
                  </pre>
                )}
              </>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

/** What the system can state as fact, separated from what a model asserted. */
function Facts({
  data,
  periodLabel,
  filters,
  active
}: {
  data: DrilldownResponse;
  periodLabel: string | null;
  filters: Filter[];
  active: ActiveFilters;
}) {
  const applied = filters
    .map((f) => {
      const v = active[f.id];
      if (!v) return null;
      if (f.type === 'daterange') {
        const dv = v as { from: string; to: string };
        if (!dv.from && !dv.to) return null;
        return `${f.label}: ${dv.from || '…'} → ${dv.to || '…'}`;
      }
      const mv = v as string[];
      return mv.length ? `${f.label}: ${mv.join(', ')}` : null;
    })
    .filter(Boolean) as string[];

  return (
    <div className="rounded-lg border bg-card p-3 text-sm space-y-1.5">
      {data.definition ? (
        <p className="text-foreground">{data.definition}</p>
      ) : (
        <p className="text-muted-foreground italic">
          No definition has been recorded for this measure, so only the facts below are shown.
        </p>
      )}
      <Row k="Source table" v={data.table} />
      <Row k="Measure" v={data.measure_expr} mono />
      {data.dimension_expr && <Row k="Grouped by" v={data.dimension_expr} mono />}
      {periodLabel && <Row k="Period" v={periodLabel} />}
      {applied.length > 0 && <Row k="Filters" v={applied.join('   •   ')} />}
      {data.computed_at && (
        <Row k="Recomputed" v={new Date(data.computed_at).toLocaleString()} />
      )}
    </div>
  );
}

function Row({ k, v, mono }: { k: string; v?: string | null; mono?: boolean }) {
  if (!v) return null;
  return (
    <div className="flex gap-2">
      <span className="text-muted-foreground shrink-0 w-28">{k}</span>
      <span className={mono ? 'font-mono text-xs break-all' : 'break-words'}>{v}</span>
    </div>
  );
}

function Note({ tone, children }: { tone: 'ok' | 'warn' | 'info'; children: React.ReactNode }) {
  const cls =
    tone === 'warn'
      ? 'border-amber-500/30 bg-amber-500/10 text-amber-800 dark:text-amber-300'
      : tone === 'ok'
        ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-800 dark:text-emerald-300'
        : 'border-border bg-muted/40 text-muted-foreground';
  return <div className={`rounded-lg border px-3 py-2 text-sm ${cls}`}>{children}</div>;
}
